import { useCallback, useMemo } from 'react'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { GeoPoint } from 'tripPlanner/types/placeAndDateTime'
import { isValidCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import DrawerListingMapPreview, { type DrawerListingMapItem } from './DrawerListingMapPreview'
import type { DrawerExperienceSearchTile } from './ExperienceListingDrawer/ExperienceDetailView'

interface Props {
  experiences: Array<DrawerExperienceSearchTile & { geoLocation: GeoPoint }>
  isLoading: boolean
  coordinates?: GeoPoint
  onSelectExperience: (experience: DrawerExperienceSearchTile) => void
  onViewOnMap?: (coordinates: GeoPoint) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}

function ExperienceMapPreview({
  experiences,
  isLoading,
  coordinates,
  onSelectExperience,
  onViewOnMap,
  itineraryData,
  onSendMessage,
}: Props) {
  const items = useMemo<Array<DrawerListingMapItem>>(() => experiences.map(experience => ({
    id: experience.offerId,
    name: experience.name,
    coordinates: experience.geoLocation,
    kind: 'experience',
  })), [experiences])
  const experiencesById = useMemo(
    () => new Map(experiences.map(experience => [experience.offerId, experience])),
    [experiences],
  )
  const handleSelectItem = useCallback((id: string) => {
    const experience = experiencesById.get(id)
    if (experience) onSelectExperience(experience)
  }, [experiencesById, onSelectExperience])

  const mapCoordinates = isValidCoordinate(coordinates) ? coordinates : items[0]?.coordinates
  const handleViewOnMap = useCallback(() => {
    if (mapCoordinates) onViewOnMap?.(mapCoordinates)
  }, [mapCoordinates, onViewOnMap])
  if (!mapCoordinates || (!isLoading && items.length === 0)) return null

  return (
    <DrawerListingMapPreview
      items={items}
      coordinates={mapCoordinates}
      onSelectItem={handleSelectItem}
      onViewOnMap={!isLoading && items.length > 0 && onViewOnMap ? handleViewOnMap : undefined}
      itineraryData={itineraryData}
      onSendMessage={onSendMessage}
    />
  )
}

export default ExperienceMapPreview
